import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import Icon from '../../../components/AppIcon';

const ClinicalOutcomeMetrics = ({ dateRange, provider, treatmentType }) => {
  const outcomeData = [
    { treatment: 'Preventive', successRate: 98.5, complications: 0.2, improvement: 95 },
    { treatment: 'Restorative', successRate: 96.8, complications: 1.8, improvement: 92 },
    { treatment: 'Endodontic', successRate: 94.2, complications: 3.5, improvement: 88 },
    { treatment: 'Periodontal', successRate: 91.5, complications: 4.2, improvement: 85 },
    { treatment: 'Prosthodontic', successRate: 95.3, complications: 2.8, improvement: 90 },
    { treatment: 'Oral Surgery', successRate: 93.7, complications: 4.8, improvement: 87 }
  ];

  const keyMetrics = [
    {
      label: 'Overall Success Rate',
      value: '95.8%',
      change: '+2.3%',
      icon: 'CheckCircle2',
      color: 'bg-success/10 text-success',
      subtitle: 'All procedures'
    },
    {
      label: 'Complication Rate',
      value: '2.9%',
      change: '-0.8%',
      icon: 'AlertTriangle',
      color: 'bg-warning/10 text-warning',
      subtitle: 'Below industry avg'
    },
    {
      label: 'Patient Improvement',
      value: '89.7%',
      change: '+4.2%',
      icon: 'TrendingUp',
      color: 'bg-primary/10 text-primary',
      subtitle: 'Reported outcomes'
    },
    {
      label: 'Follow-up Rate',
      value: '87%',
      change: '+5%',
      icon: 'Calendar',
      color: 'bg-muted text-muted-foreground',
      subtitle: 'Post-treatment'
    }
  ];

  const beforeAfterMetrics = [
    { metric: 'Pain Level Reduction', before: 7.2, after: 1.8, improvement: 75 },
    { metric: 'Functional Improvement', before: 4.5, after: 8.9, improvement: 98 },
    { metric: 'Aesthetic Satisfaction', before: 3.8, after: 9.1, improvement: 140 },
    { metric: 'Quality of Life', before: 5.2, after: 8.7, improvement: 67 }
  ];

  const patientReportedOutcomes = [
    { category: 'Excellent', count: 245, percentage: 68 },
    { category: 'Good', count: 89, percentage: 25 },
    { category: 'Fair', count: 18, percentage: 5 },
    { category: 'Poor', count: 8, percentage: 2 }
  ];

  return (
    <div className="bg-card rounded-xl border border-border shadow-elevation-2 p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg md:text-xl font-semibold text-foreground flex items-center gap-2">
          <Icon name="Activity" size={20} className="text-primary" />
          Clinical Outcome Metrics
        </h2>
        <span className="text-xs text-muted-foreground">
          {dateRange === 'day' ? 'Today' : dateRange === 'week' ? 'This Week' : dateRange === 'month' ? 'This Month' : dateRange === 'quarter' ? 'This Quarter' : 'This Year'}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6">
        {keyMetrics?.map((metric, index) => (
          <div key={index} className="bg-muted/50 rounded-lg p-3 border border-border">
            <div className="flex items-center justify-between mb-2">
              <div className={`flex items-center justify-center w-8 h-8 rounded-lg ${metric?.color}`}>
                <Icon name={metric?.icon} size={16} />
              </div>
              <span className="text-xs font-semibold text-success">{metric?.change}</span>
            </div>
            <p className="text-xs text-muted-foreground mb-1">{metric?.label}</p>
            <p className="text-xl font-bold text-foreground">{metric?.value}</p>
            <p className="text-xs text-muted-foreground mt-1">{metric?.subtitle}</p>
          </div>
        ))}
      </div>

      <div className="mb-6">
        <h3 className="text-sm font-semibold text-foreground mb-4">Treatment Success Rates & Complications</h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={outcomeData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis
                dataKey="treatment"
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '11px' }}
                angle={-15}
                textAnchor="end"
                height={60}
              />
              <YAxis
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '11px' }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--color-card)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                  color: 'var(--color-foreground)'
                }}
              />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              <Bar dataKey="successRate" fill="#059669" radius={[8, 8, 0, 0]} name="Success Rate (%)" />
              <Bar dataKey="complications" fill="#DC2626" radius={[8, 8, 0, 0]} name="Complication Rate (%)" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-foreground mb-4">Before/After Improvement Tracking</h3>
        <div className="space-y-4">
          {beforeAfterMetrics?.map((metric, index) => (
            <div key={index} className="bg-muted/50 rounded-lg p-4 border border-border">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-foreground">{metric?.metric}</span>
                <div className="flex items-center gap-2">
                  <Icon name="TrendingUp" size={14} className="text-success" />
                  <span className="text-sm font-bold text-success">+{metric?.improvement}%</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Before Treatment</p>
                  <p className="text-lg font-semibold text-foreground">{metric?.before}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">After Treatment</p>
                  <p className="text-lg font-semibold text-success">{metric?.after}</p>
                </div>
              </div>
              <div className="relative w-full bg-muted rounded-full h-2 overflow-hidden">
                <div
                  className="absolute h-full bg-error transition-all duration-500"
                  style={{ width: `${(metric?.before / 10) * 100}%` }}
                />
                <div
                  className="absolute h-full bg-success transition-all duration-500"
                  style={{ width: `${(metric?.after / 10) * 100}%`, left: `${(metric?.before / 10) * 100}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 pt-6 border-t border-border">
        <h3 className="text-sm font-semibold text-foreground mb-3">Patient-Reported Outcomes</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {patientReportedOutcomes?.map((outcome, index) => (
            <div key={index} className="bg-muted/50 rounded-lg p-3 border border-border text-center">
              <p className="text-2xl font-bold text-foreground mb-1">{outcome?.count}</p>
              <p className="text-xs text-muted-foreground mb-1">{outcome?.category}</p>
              <p className="text-xs font-semibold text-primary">{outcome?.percentage}%</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ClinicalOutcomeMetrics;